import { type IntlShape } from 'dibs-react-intl';
import { CM, type MeasurementUnit } from 'dibs-intl/exports/units';
import { hasKey } from 'dibs-ts-utils/exports/hasKey.ts';

import { pdpDimensionMessages } from './pdpDimensionMessages.ts';

type PdpDimensionValue = {
    detailKey?: string | null;
    measurementDisplayName?: string | null;
    inchesValue?: string | null;
    metricValue?: string | null;
    displayName?: string | null;
};

const extractMeasurementNumericValue = (formattedValue: string): number | null => {
    const match = formattedValue.match(/[\d.,]+/);
    if (!match) {
        return null;
    }

    const numericValue = parseFloat(match[0].replace(',', '.'));
    return Number.isFinite(numericValue) ? numericValue : null;
};

const getDimensionLabel = (
    detailKey: string | null | undefined,
    measurementDisplayName: string | null | undefined,
    formatMessage: IntlShape['formatMessage']
): string | null => {
    if (detailKey && hasKey(pdpDimensionMessages, detailKey)) {
        return formatMessage(pdpDimensionMessages[detailKey]);
    }

    return measurementDisplayName || null;
};

const formatMeasurementWithUnit = (
    formattedValue: string,
    unit: MeasurementUnit
): string | null => {
    const numericValue = extractMeasurementNumericValue(formattedValue);
    if (numericValue === null) {
        return null;
    }

    const unitSuffix = unit === CM ? 'cm' : 'in';
    // Round to at most one decimal; unlike toFixed(1), whole numbers render without ".0".
    return `${Math.round(numericValue * 10) / 10} ${unitSuffix}`;
};

export const getPdpDimensionSegments = (
    detailValues: ReadonlyArray<PdpDimensionValue | null | undefined> | null | undefined,
    unit: MeasurementUnit,
    formatMessage: IntlShape['formatMessage']
): string[] =>
    (detailValues || []).reduce<string[]>((segments, value) => {
        if (!value) {
            return segments;
        }

        const rawMeasurement = unit === CM ? value.metricValue : value.inchesValue;
        const measurement = rawMeasurement ? formatMeasurementWithUnit(rawMeasurement, unit) : null;

        if (measurement) {
            const label = getDimensionLabel(
                value.detailKey,
                value.measurementDisplayName,
                formatMessage
            );
            segments.push(label ? `${label} ${measurement}` : measurement);
            return segments;
        }

        if (value.displayName) {
            segments.push(value.displayName);
        }

        return segments;
    }, []);

export const formatPdpDimensionValues = (
    detailValues: ReadonlyArray<PdpDimensionValue | null | undefined> | null | undefined,
    unit: MeasurementUnit,
    formatMessage: IntlShape['formatMessage']
): string => getPdpDimensionSegments(detailValues, unit, formatMessage).join(', ');
